import type { SupportedLocale } from '@erp/shared';

export const datevPaymentTranslations = {
  de: {
    type: 'Exportumfang',
    invoices: 'Ausgangsrechnungen',
    vendorInvoices: 'Eingangsrechnungen',
    payments: 'Zahlungen und Ausgleich',
    hint: 'Dieser Stapel enthält ausschließlich festgeschriebene Zahlungs-, Ausgleichs- und Übertragsbuchungen. Rechnungen werden dabei nicht erneut als Erlös oder Aufwand berechnet. EUR-Hauptbuchbeträge werden je Buchung ausgeglichen auf Cent übergeleitet; ursprüngliche Werte bleiben im Journal.',
    access: 'Die Funktion oder Ihre aktuellen Lese- und Exportrechte sind nicht mehr verfügbar.',
    rounding:
      'Hauptbuch: {{source}} EUR · Export: {{exported}} EUR · Centüberleitung: {{difference}} EUR',
    configuration: 'Steuerautomatik für den Zahlungsexport prüfen',
    configurationHint:
      'Tragen Sie die im DATEV-Zielmandanten tatsächlich als automatisch steuerrechnend eingerichteten Konten ein, getrennt durch Leerzeichen oder Zeilenumbrüche. Für diese Konten wird die Steuerautomatik aufgehoben, da die Steuer bereits im Hauptbuch gebucht ist. Leer und bestätigt bedeutet ausdrücklich: keine Automatikkonten. Lassen Sie die Zuordnung bei Bedarf durch Ihre Buchhaltung prüfen.',
    automaticAccounts: 'DATEV-Automatikkonten',
    reviewed: 'Die Automatikkonten des Zielmandanten sind geprüft.',
    invalidConfiguration: 'Kontonummern und Prüfung bestätigen. Die Eingaben bleiben erhalten.',
    configurationOwner:
      'Eine berechtigte Administration muss die Steuerautomatik einmal prüfen und speichern.',
    issues: {
      PAYMENT_ACCOUNTS_UNREVIEWED:
        'Die Steuerautomatik des DATEV-Zielmandanten muss zuerst geprüft werden.',
      PAYMENT_ACCOUNT_INVALID: 'Ein Hauptbuchkonto hat keine gültige numerische DATEV-Kontonummer.',
      PAYMENT_DIMENSION_CONFLICT:
        'Widersprüchliche Kostenstellen oder Kostenträger verhindern den Export.',
      PAYMENT_JOURNAL_INVALID: 'Eine Buchung ist für den ausgeglichenen EUR-Export nicht geeignet.',
      PAYMENT_ROUNDING:
        'Die Centüberleitung weicht von den vierstelligen Hauptbuchbeträgen ab; prüfen Sie die angezeigten Summen.',
    },
  },
  en: {
    type: 'Export scope',
    invoices: 'Customer invoices',
    vendorInvoices: 'Supplier invoices',
    payments: 'Payments and settlement',
    hint: 'This batch contains only posted payment, settlement and own-transfer entries. It does not calculate invoice revenue or expenses again. EUR ledger amounts are converted to balanced cents per journal; original values remain in the journal.',
    access: 'The feature or your current read and export permissions are no longer available.',
    rounding:
      'Ledger: {{source}} EUR · Export: {{exported}} EUR · Cent adjustment: {{difference}} EUR',
    configuration: 'Review tax automation for payment exports',
    configurationHint:
      'Enter accounts actually configured for automatic tax in the target DATEV client, separated by spaces or line breaks. Tax automation is suppressed for these accounts because the ledger already contains the tax entries. An empty confirmed list explicitly means no automatic accounts. Have your accounting team review the mapping when needed.',
    automaticAccounts: 'DATEV automatic tax accounts',
    reviewed: 'The target client’s automatic accounts have been reviewed.',
    invalidConfiguration:
      'Check account numbers and confirm the review. Your entries remain available.',
    configurationOwner:
      'An authorized administrator must review and save the tax automation configuration once.',
    issues: {
      PAYMENT_ACCOUNTS_UNREVIEWED: 'Review the target DATEV client’s tax automation first.',
      PAYMENT_ACCOUNT_INVALID: 'A ledger account has no valid numeric DATEV account number.',
      PAYMENT_DIMENSION_CONFLICT: 'Conflicting cost centers or cost objects prevent export.',
      PAYMENT_JOURNAL_INVALID: 'A journal is unsuitable for balanced EUR export.',
      PAYMENT_ROUNDING:
        'Cent conversion differs from the four-decimal ledger amounts; review the displayed totals.',
    },
  },
  fr: {
    type: 'Périmètre de l’export',
    invoices: 'Factures clients',
    vendorInvoices: 'Factures fournisseurs',
    payments: 'Paiements et rapprochements',
    hint: 'Ce lot contient uniquement les écritures validées de paiement, de règlement et de virement interne. Il ne recalcule pas les produits ou charges des factures. Les montants du grand livre en EUR sont convertis en centimes équilibrés par écriture ; les valeurs originales restent dans le journal.',
    access: 'La fonction ou vos droits actuels de lecture et d’export ne sont plus disponibles.',
    rounding:
      'Grand livre : {{source}} EUR · Export : {{exported}} EUR · Ajustement au centime : {{difference}} EUR',
    configuration: 'Vérifier l’automatisation fiscale des paiements',
    configurationHint:
      'Saisissez les comptes réellement configurés pour le calcul automatique des taxes dans le dossier DATEV cible, séparés par des espaces ou des retours à la ligne. Ce calcul est désactivé pour ces comptes car les taxes sont déjà comptabilisées. Une liste vide et confirmée signifie explicitement aucun compte automatique. Faites vérifier la correspondance par votre comptabilité si nécessaire.',
    automaticAccounts: 'Comptes automatiques DATEV',
    reviewed: 'Les comptes automatiques du dossier cible ont été vérifiés.',
    invalidConfiguration:
      'Vérifiez les numéros de compte et confirmez le contrôle. Vos saisies sont conservées.',
    configurationOwner:
      'Une administration autorisée doit vérifier et enregistrer une fois la configuration fiscale.',
    issues: {
      PAYMENT_ACCOUNTS_UNREVIEWED:
        'Vérifiez d’abord l’automatisation fiscale du dossier DATEV cible.',
      PAYMENT_ACCOUNT_INVALID: 'Un compte n’a pas de numéro DATEV numérique valide.',
      PAYMENT_DIMENSION_CONFLICT:
        'Des centres ou objets de coûts contradictoires empêchent l’export.',
      PAYMENT_JOURNAL_INVALID: 'Une écriture ne permet pas un export équilibré en EUR.',
      PAYMENT_ROUNDING:
        'La conversion au centime diffère des montants à quatre décimales ; vérifiez les totaux affichés.',
    },
  },
  es: {
    type: 'Ámbito de exportación',
    invoices: 'Facturas de clientes',
    vendorInvoices: 'Facturas de proveedores',
    payments: 'Pagos y compensaciones',
    hint: 'Este lote contiene únicamente asientos definitivos de pagos, compensaciones y traspasos propios. No vuelve a calcular ingresos ni gastos de las facturas. Los importes del mayor en EUR se convierten a céntimos equilibrados por asiento; los valores originales permanecen en el diario.',
    access: 'La función o sus permisos actuales de lectura y exportación ya no están disponibles.',
    rounding:
      'Mayor: {{source}} EUR · Exportación: {{exported}} EUR · Ajuste a céntimos: {{difference}} EUR',
    configuration: 'Revisar el cálculo fiscal automático de pagos',
    configurationHint:
      'Introduzca las cuentas que realmente calculan impuestos automáticamente en el cliente DATEV de destino, separadas por espacios o saltos de línea. Se desactiva ese cálculo porque el mayor ya contiene los asientos fiscales. Una lista vacía y confirmada significa expresamente que no hay cuentas automáticas. Si es necesario, solicite la revisión de su contabilidad.',
    automaticAccounts: 'Cuentas automáticas DATEV',
    reviewed: 'Se han revisado las cuentas automáticas del cliente de destino.',
    invalidConfiguration:
      'Revise los números de cuenta y confirme la comprobación. Se conservan sus datos.',
    configurationOwner:
      'Una administración autorizada debe revisar y guardar una vez la configuración fiscal.',
    issues: {
      PAYMENT_ACCOUNTS_UNREVIEWED:
        'Revise primero el cálculo fiscal automático del cliente DATEV de destino.',
      PAYMENT_ACCOUNT_INVALID: 'Una cuenta no tiene un número DATEV numérico válido.',
      PAYMENT_DIMENSION_CONFLICT:
        'Centros u objetos de coste contradictorios impiden la exportación.',
      PAYMENT_JOURNAL_INVALID: 'Un asiento no permite una exportación equilibrada en EUR.',
      PAYMENT_ROUNDING:
        'La conversión a céntimos difiere de los importes del mayor con cuatro decimales; revise los totales mostrados.',
    },
  },
  tr: {
    type: 'Dışa aktarma kapsamı',
    invoices: 'Müşteri faturaları',
    vendorInvoices: 'Tedarikçi faturaları',
    payments: 'Ödemeler ve mahsuplar',
    hint: 'Bu dosya yalnızca kesinleşmiş ödeme, mahsup ve kendi hesaplarınız arasındaki transfer kayıtlarını içerir. Fatura gelir veya giderini yeniden hesaplamaz. EUR büyük defter tutarları kayıt başına dengeli kuruşlara dönüştürülür; asıl değerler yevmiyede korunur.',
    access: 'Özellik veya mevcut okuma ve dışa aktarma yetkileriniz artık kullanılamıyor.',
    rounding:
      'Büyük defter: {{source}} EUR · Dışa aktarma: {{exported}} EUR · Kuruş düzeltmesi: {{difference}} EUR',
    configuration: 'Ödeme dışa aktarımının vergi otomatiğini inceleyin',
    configurationHint:
      'Hedef DATEV müşterisinde gerçekten otomatik vergi hesaplayan hesapları boşluk veya satır sonuyla ayırarak girin. Vergi zaten büyük deftere kaydedildiğinden bu hesaplarda otomatik hesaplama kapatılır. Boş ve onaylanmış liste açıkça otomatik hesap olmadığını belirtir. Gerekirse eşleştirmeyi muhasebenize kontrol ettirin.',
    automaticAccounts: 'DATEV otomatik vergi hesapları',
    reviewed: 'Hedef müşterinin otomatik hesapları incelendi.',
    invalidConfiguration:
      'Hesap numaralarını kontrol edin ve incelemeyi onaylayın. Girişleriniz korunur.',
    configurationOwner:
      'Yetkili yönetim vergi otomatiği yapılandırmasını bir kez inceleyip kaydetmelidir.',
    issues: {
      PAYMENT_ACCOUNTS_UNREVIEWED: 'Önce hedef DATEV müşterisinin vergi otomatiğini inceleyin.',
      PAYMENT_ACCOUNT_INVALID: 'Bir hesabın geçerli sayısal DATEV hesap numarası yok.',
      PAYMENT_DIMENSION_CONFLICT:
        'Çelişen maliyet merkezleri veya nesneleri dışa aktarımı engelliyor.',
      PAYMENT_JOURNAL_INVALID: 'Bir kayıt dengeli EUR dışa aktarımına uygun değil.',
      PAYMENT_ROUNDING:
        'Kuruşa dönüştürme dört ondalıklı defter tutarlarından farklıdır; gösterilen toplamları inceleyin.',
    },
  },
} as const satisfies Record<SupportedLocale, Record<string, unknown>>;
